import React, { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Package } from "lucide-react";

export default function WholesalePriceImportDialog({ open, onOpenChange, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    setResult(null);
    
    try {
      const fd = new FormData();
      fd.append("file", file);
      
      const { data } = await api.post("/products/import-wholesale-prices", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setResult(data);
      
      if (data.updated > 0) {
        toast.success(`Updated wholesale prices for ${data.updated} products`);
        onSuccess?.();
      } else {
        toast.info("No products were updated");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await api.get("/products/wholesale-prices/template", {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "wholesale_prices_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="wholesale-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-600" />
            Import Wholesale Prices
          </DialogTitle>
          <DialogDescription>
            Upload an Excel file to update wholesale prices for products.
            Only the wholesale price field will be updated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Instructions */}
          <div className="bg-muted/50 rounded-lg p-4 text-sm space-y-2">
            <div className="font-medium">Excel Format (3 columns):</div>
            <div className="grid grid-cols-3 gap-2 text-xs bg-white rounded border p-2">
              <div className="font-medium text-center">Auto Barcode</div>
              <div className="font-medium text-center">Product</div>
              <div className="font-medium text-center">Wholesale Price</div>
            </div>
            <ul className="text-xs text-muted-foreground space-y-1 mt-2">
              <li>• <strong>Auto Barcode</strong>: Used to match products (required)</li>
              <li>• <strong>Product</strong>: For your reference only</li>
              <li>• <strong>Wholesale Price</strong>: The price to set (required)</li>
            </ul>
          </div>

          {/* Download Template */}
          <Button
            variant="outline"
            className="w-full"
            onClick={handleDownloadTemplate}
            disabled={downloading}
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? "Downloading..." : "Download Template with Current Products"}
          </Button>

          {/* Upload Section */}
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <input
              type="file"
              ref={fileRef}
              accept=".xls,.xlsx"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              className="hidden"
              data-testid="wholesale-file-input"
            />
            <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <Button
              variant="default"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="upload-wholesale-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Uploading..." : "Upload Excel File"}
            </Button>
            <div className="text-xs text-muted-foreground mt-2">
              Accepts .xls and .xlsx files
            </div>
          </div>

          {/* Results */}
          {result && (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 font-medium">
                {result.updated > 0 ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                )}
                Import Results
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between p-2 bg-green-50 rounded">
                  <span>Updated:</span>
                  <span className="font-mono font-semibold text-green-700">{result.updated}</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>Skipped (no barcode):</span>
                  <span className="font-mono">{result.skipped_no_barcode}</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>Skipped (no price):</span>
                  <span className="font-mono">{result.skipped_no_price}</span>
                </div>
                <div className="flex justify-between p-2 bg-amber-50 rounded">
                  <span>Not found:</span>
                  <span className="font-mono text-amber-700">{result.not_found_count}</span>
                </div>
              </div>

              {result.not_found_count > 0 && result.not_found?.length > 0 && (
                <div className="text-xs">
                  <div className="font-medium text-amber-700 mb-1">Products not found (first 20):</div>
                  <div className="max-h-24 overflow-auto bg-amber-50/50 rounded p-2 space-y-0.5">
                    {result.not_found.map((p) => (
                      <div key={p.auto_barcode || p.product} className="flex gap-2">
                        <span className="font-mono">{p.auto_barcode}</span>
                        <span className="text-muted-foreground truncate">{p.product}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
